"use client";

/**
 * The pill's TASK MODE (spec 2026-09-20 one-composer-everywhere §4, R-F2):
 * only what the composer KNOWS changes on a task's screen, never how it looks.
 * Derived, never stored: the route and the selected task are the truth.
 *
 * `route` and `on` are two things on purpose. `route` is "the pill is this
 * screen's composer" (any task route with the flag on): the focus law stands
 * down there and a send never navigates. `on` is "a CREATED task is in view":
 * the lock, the lanes, the pickers, the shelf and the key's Stop apply. The
 * new-task page, and a pending placeholder the header's Computer button
 * allocated, are the route without task mode.
 *
 * The route is the rule, never the selection alone: a selectedTaskId outlives
 * the page it was picked on (R-P-8), which is why the home's model chip takes
 * `forNewTask`. `onTaskRoute` matches a bare /task too: the task page rewrites
 * its URL with replaceState and the router's pathname can lag behind it.
 */
import { useMemo } from "react";
import { usePathname } from "next/navigation";
import { isDockOnTaskEnabled } from "@/components/composer/dock-on-task-flags";
import { onTaskRoute } from "@/lib/composer/screen-context";
import { useTaskStore } from "@/stores/taskStore";

export interface PillTaskMode {
  /** On /task or /task/* with one-composer-everywhere on. */
  route: boolean;
  /** A CREATED task is in view on that route (never the pending placeholder). */
  on: boolean;
  /** That task, or null. */
  taskId: string | null;
}

export function usePillTaskMode(): PillTaskMode {
  const pathname = usePathname();
  const selectedTaskId = useTaskStore((s) => s.selectedTaskId);
  const pendingNewTaskId = useTaskStore((s) => s.pendingNewTaskId);
  const route = isDockOnTaskEnabled() && onTaskRoute(pathname ?? "");
  const taskId =
    route && selectedTaskId && selectedTaskId !== pendingNewTaskId
      ? selectedTaskId
      : null;
  return useMemo(
    () => ({ route, on: taskId !== null, taskId }),
    [route, taskId]
  );
}
